import type {
  CloudBookSummary,
  CloudHttpRequest,
  CloudHttpResponse,
  CloudTokenResponse,
  DesktopCloudPlatform,
  DesktopLocalCopy,
  DeviceAuthorizeResponse,
} from './types';
import type { BookBetaReadProgramConfig } from '../betaReadProgram';
import type { BetaReaderQuestionnaireQuestion } from '../betaReaderQuestionnaire';

const LS_USER_ID = 'jw_desktop_cloud_user_id';
const LS_ACCESS = 'jw_desktop_cloud_access';
const LS_ACCESS_EXPIRES_AT = 'jw_desktop_cloud_access_expires_at';
const LS_USER_EMAIL = 'jw_desktop_cloud_user_email';

const CONNECT_PATH = '/account/desktop-device';
/** One-time code from heyjustwrite.com SPA SSO handoff (`/api/desktop/spa/handoff`). */
const SPA_HANDOFF_QUERY = 'spa_handoff';
const SPA_SSO_SKIP_KEY = 'jw_spa_sso_skip';

export type DesktopChapterSnapshot = {
  title: string;
  content: unknown;
  originalContent: unknown;
  rev: number;
  updatedAt: string;
};

export type DesktopChapterPatchResult =
  | { ok: true; rev: number; updatedAt: string }
  | {
      ok: false;
      status: 409;
      remote: { title: string; content: unknown; rev: number; updatedAt: string } | null;
    }
  | { ok: false; status: number; error: string; offline?: boolean };

export type DesktopBetaInvite = {
  id: string;
  inviteeEmail: string;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
  status: string;
  acceptedAt: string | null;
  readerName: string | null;
};

export type DesktopBetaApplication = {
  id: string;
  status: 'pending' | 'approved' | 'rejected';
  venmoHandle: string | null;
  createdAt: string;
  reviewedAt: string | null;
  applicantEmail: string;
  applicantName: string | null;
  emailVerified: boolean;
};

export type DesktopBetaEndSurvey = {
  version: number;
  questions: BetaReaderQuestionnaireQuestion[];
  updatedAt: string;
};

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

async function parseJson<T>(res: CloudHttpResponse): Promise<T> {
  try {
    return JSON.parse(res.body) as T;
  } catch {
    return {} as T;
  }
}

export class DesktopCloudClient {
  constructor(private readonly platform: DesktopCloudPlatform) {}

  async isWebCloudConfigured(): Promise<boolean> {
    const base = await this.platform.getBaseUrl();
    return Boolean(base?.trim());
  }

  isSignedIn(): boolean {
    return Boolean(localStorage.getItem(LS_ACCESS)?.trim() && localStorage.getItem(LS_USER_ID)?.trim());
  }

  getUserId(): string | null {
    const id = localStorage.getItem(LS_USER_ID);
    return id?.trim() ? id.trim() : null;
  }

  getUserEmail(): string | null {
    const raw = localStorage.getItem(LS_USER_EMAIL);
    return raw?.trim() ? raw.trim() : null;
  }

  private persistAccess(accessToken: string, expiresIn: number): void {
    const expiresAt = Date.now() + Math.max(0, expiresIn) * 1000;
    localStorage.setItem(LS_ACCESS, accessToken);
    localStorage.setItem(LS_ACCESS_EXPIRES_AT, String(expiresAt));
  }

  private async persistSession(data: CloudTokenResponse): Promise<void> {
    this.persistAccess(data.accessToken, data.expiresIn);
    await this.platform.setRefreshToken(data.refreshToken);
    localStorage.setItem(LS_USER_ID, data.user.id);
    localStorage.setItem(LS_USER_EMAIL, data.user.email);
  }

  private async getBaseUrl(): Promise<string> {
    const raw = await this.platform.getBaseUrl();
    const trimmed = typeof raw === 'string' ? raw.trim().replace(/\/+$/, '') : '';
    if (!trimmed) {
      throw new Error(
        'DESKTOP_WEB_APP_URL is not set. Add it to apps/desktop-v2/.env or set NEXTAUTH_URL in apps/web/.env.'
      );
    }
    return trimmed;
  }

  private httpRequest(input: CloudHttpRequest): Promise<CloudHttpResponse> {
    return this.platform.httpRequest(input);
  }

  async restoreSessionFromStorage(): Promise<boolean> {
    if (!(await this.isWebCloudConfigured())) return false;
    const access = localStorage.getItem(LS_ACCESS);
    const uid = localStorage.getItem(LS_USER_ID);
    if (!access?.trim() || !uid?.trim()) return false;
    const expRaw = localStorage.getItem(LS_ACCESS_EXPIRES_AT);
    const exp = expRaw ? Number(expRaw) : 0;
    if (exp > Date.now() + 15_000) return true;
    try {
      await this.refreshSession();
      return this.isSignedIn();
    } catch {
      this.fullSignOut();
      return false;
    }
  }

  async refreshSession(): Promise<void> {
    const base = await this.getBaseUrl();
    const refreshToken = await this.platform.getRefreshToken();
    if (!refreshToken?.trim()) throw new Error('No refresh token');
    const res = await this.httpRequest({
      url: `${base}/api/mobile/auth/refresh`,
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken: refreshToken.trim() }),
    });
    const data = await parseJson<CloudTokenResponse & { error?: string }>(res);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(data.error || `Refresh failed (${res.status})`);
    }
    await this.persistSession(data);
  }

  async getValidAccessToken(): Promise<string> {
    const access = localStorage.getItem(LS_ACCESS)?.trim();
    const expRaw = localStorage.getItem(LS_ACCESS_EXPIRES_AT);
    const exp = expRaw ? Number(expRaw) : 0;
    if (access && exp > Date.now() + 30_000) return access;
    await this.refreshSession();
    const next = localStorage.getItem(LS_ACCESS)?.trim();
    if (!next) throw new Error('Not signed in');
    return next;
  }

  private async pollDeviceToken(
    deviceCode: string
  ): Promise<'ready' | 'pending'> {
    const base = await this.getBaseUrl();
    const tr = await this.httpRequest({
      url: `${base}/api/desktop/device/token`,
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ deviceCode }),
    });
    const tj = await parseJson<
      CloudTokenResponse & { authorization_pending?: boolean; error?: string }
    >(tr);
    if (tr.status >= 200 && tr.status < 300 && tj.accessToken && tj.refreshToken && tj.user?.id) {
      await this.persistSession(tj);
      return 'ready';
    }
    if (tr.status === 400 && tj.authorization_pending) return 'pending';
    throw new Error(tj.error || `Token exchange failed (${tr.status})`);
  }

  private async exchangeDeviceCode(deviceCode: string): Promise<void> {
    const result = await this.pollDeviceToken(deviceCode);
    if (result !== 'ready') {
      throw new Error('Authorization still pending');
    }
  }

  /**
   * Consume `?spa_handoff=` from the current URL if present (web SPA SSO return).
   * Returns true when a session was established from the handoff code.
   */
  async consumeSpaHandoffFromUrl(): Promise<boolean> {
    if (typeof window === 'undefined' || !window.location?.href) return false;
    const url = new URL(window.location.href);
    const code = url.searchParams.get(SPA_HANDOFF_QUERY)?.trim() ?? '';
    if (!code) return false;

    url.searchParams.delete(SPA_HANDOFF_QUERY);
    url.searchParams.delete('auth_error');
    const cleaned = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, '', cleaned || '/');

    try {
      sessionStorage.removeItem(SPA_SSO_SKIP_KEY);
    } catch {
      /* ignore */
    }
    await this.exchangeDeviceCode(code);
    return true;
  }

  /**
   * Web SPA sign-in via shared NextAuth session on heyjustwrite.com (no pairing code).
   * Navigates same-tab to the handoff endpoint when no return code is present.
   * @returns `'signed_in'` after exchanging a handoff code, or `'redirecting'` when leaving the page.
   */
  async signInWithSpaSession(options?: { explicit?: boolean }): Promise<'signed_in' | 'redirecting'> {
    if (await this.consumeSpaHandoffFromUrl()) {
      return 'signed_in';
    }
    if (typeof window === 'undefined') {
      throw new Error('SPA session sign-in requires a browser');
    }
    if (options?.explicit) {
      try {
        sessionStorage.removeItem(SPA_SSO_SKIP_KEY);
      } catch {
        /* ignore */
      }
    }
    const base = await this.getBaseUrl();
    const returnUrl = new URL(window.location.href);
    returnUrl.searchParams.delete(SPA_HANDOFF_QUERY);
    returnUrl.searchParams.delete('auth_error');
    const handoff = new URL(`${base}/api/desktop/spa/handoff`);
    handoff.searchParams.set('return_to', returnUrl.toString());
    window.location.assign(handoff.toString());
    return 'redirecting';
  }

  /**
   * On SPA boot: restore tokens, exchange a handoff return, or silently SSO once
   * when the user already has a heyjustwrite.com session.
   */
  async ensureSpaSession(): Promise<boolean> {
    if (!(await this.isWebCloudConfigured())) return false;
    if (await this.restoreSessionFromStorage()) return true;
    if (await this.consumeSpaHandoffFromUrl()) return true;

    if (typeof window === 'undefined') return false;
    const url = new URL(window.location.href);
    if (url.searchParams.has('auth_error')) return false;
    try {
      if (sessionStorage.getItem(SPA_SSO_SKIP_KEY) === '1') return false;
      sessionStorage.setItem(SPA_SSO_SKIP_KEY, '1');
    } catch {
      /* ignore */
    }
    await this.signInWithSpaSession({ explicit: false });
    return false;
  }

  /** Device pairing: opens system browser, polls until user approves on heyjustwrite.com. */
  async signInWithWebFlow(): Promise<void> {
    const base = await this.getBaseUrl();
    const res = await this.httpRequest({
      url: `${base}/api/desktop/device/authorize`,
      method: 'POST',
    });
    const data = await parseJson<DeviceAuthorizeResponse & { error?: string }>(res);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(data.error || `Authorize failed (${res.status})`);
    }

    const approveUrl = `${base}${CONNECT_PATH}?user_code=${encodeURIComponent(data.user_code)}`;
    await this.platform.openExternalUrl(approveUrl);

    const deadline = Date.now() + data.expires_in * 1000;
    const intervalMs = Math.max(2000, (data.interval ?? 5) * 1000);

    while (Date.now() < deadline) {
      await sleep(intervalMs);
      const result = await this.pollDeviceToken(data.device_code);
      if (result === 'ready') return;
    }
    throw new Error('Timed out waiting for approval in the browser.');
  }

  private async authorizedFetch(path: string, init?: Omit<CloudHttpRequest, 'url'>): Promise<CloudHttpResponse> {
    const base = await this.getBaseUrl();
    const doFetch = async (token: string) =>
      this.httpRequest({
        url: `${base}${path}`,
        method: init?.method,
        headers: {
          ...(init?.headers ?? {}),
          authorization: `Bearer ${token}`,
        },
        body: init?.body,
      });

    let token = await this.getValidAccessToken();
    let res = await doFetch(token);
    if (res.status === 401) {
      await this.refreshSession();
      token = await this.getValidAccessToken();
      res = await doFetch(token);
    }
    return res;
  }

  /**
   * Generic authorized JSON request for desktop-only surfaces that don't have
   * a dedicated wrapper (e.g. the sys_admin feedback panel). Returns the
   * status so callers can branch on 403 without catching.
   */
  async authorizedJson<T>(
    path: string,
    init?: Omit<CloudHttpRequest, 'url'>
  ): Promise<{ status: number; body: T }> {
    const res = await this.authorizedFetch(path, init);
    const body = await parseJson<T>(res);
    return { status: res.status, body };
  }

  async bookExists(bookId: string): Promise<boolean> {
    const id = bookId.trim();
    if (!id) return false;
    const res = await this.authorizedFetch(`/api/desktop/books/${encodeURIComponent(id)}`);
    if (res.status === 404) return false;
    if (res.status < 200 || res.status >= 300) {
      const body = await parseJson<{ error?: string }>(res);
      throw new Error(body.error || `Book check failed (${res.status})`);
    }
    return true;
  }

  async getBookUpdatedAt(bookId: string): Promise<string | null> {
    const res = await this.authorizedFetch(
      `/api/desktop/books/${encodeURIComponent(bookId.trim())}/timestamps`
    );
    const body = await parseJson<{ error?: string; bookUpdatedAt?: string | null }>(res);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(body.error || `Timestamps failed (${res.status})`);
    }
    return body.bookUpdatedAt ?? null;
  }

  async getChapterTimestamps(bookId: string): Promise<Record<string, string>> {
    const res = await this.authorizedFetch(
      `/api/desktop/books/${encodeURIComponent(bookId.trim())}/timestamps`
    );
    const body = await parseJson<{ error?: string; chapters?: Record<string, string> }>(res);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(body.error || `Timestamps failed (${res.status})`);
    }
    return body.chapters ?? {};
  }

  async createBook(input?: {
    title?: string;
    author?: string;
    description?: string;
  }): Promise<{ id: string; firstChapterId: string; versionId: string }> {
    const res = await this.authorizedFetch('/api/books', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        title: input?.title ?? '',
        author: input?.author ?? '',
        description: input?.description ?? '',
      }),
    });
    const body = await parseJson<{
      error?: string;
      id?: string;
      firstChapterId?: string;
      versionId?: string;
    }>(res);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(body.error || `Create book failed (${res.status})`);
    }
    const id = typeof body.id === 'string' ? body.id.trim() : '';
    if (!id) {
      throw new Error('Create book returned no id');
    }
    return {
      id,
      firstChapterId: typeof body.firstChapterId === 'string' ? body.firstChapterId : '',
      versionId: typeof body.versionId === 'string' ? body.versionId : '',
    };
  }

  async listOwnedBooks(): Promise<CloudBookSummary[]> {
    const res = await this.authorizedFetch('/api/desktop/books');
    const body = await parseJson<{
      error?: string;
      books?: Array<{
        id: string;
        title: string;
        author?: string;
        updatedAt: string;
        chapterCount?: number;
      }>;
    }>(res);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(body.error || `List books failed (${res.status})`);
    }
    return (body.books ?? []).map((b) => ({
      id: b.id,
      title: b.title,
      author: typeof b.author === 'string' ? b.author : '',
      updatedAt: b.updatedAt,
      chapterCount: typeof b.chapterCount === 'number' ? b.chapterCount : 0,
    }));
  }

  async loadBook(bookId: string): Promise<unknown> {
    const res = await this.authorizedFetch(`/api/desktop/books/${encodeURIComponent(bookId.trim())}`);
    const body = await parseJson<{ error?: string; book?: unknown }>(res);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(body.error || `Load failed (${res.status})`);
    }
    if (!body.book) {
      throw new Error('Book payload missing');
    }
    return body.book;
  }

  async listBookVersions(bookId: string): Promise<
    Array<{ id: string; name: string; status: string; frozenAt?: string | null; chapterCount?: number }>
  > {
    const res = await this.authorizedFetch(`/api/books/${encodeURIComponent(bookId.trim())}/versions`);
    const body = await parseJson<{
      error?: string;
      versions?: Array<{
        id: string;
        name: string;
        status: string;
        frozenAt?: string | null;
        chapterCount?: number;
      }>;
    }>(res);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(body.error || `Versions failed (${res.status})`);
    }
    return Array.isArray(body.versions) ? body.versions : [];
  }

  /** Continuous chapter change history (Premier) — not frozen BookVersion snapshots. */
  async listChapterChangeHistory(
    bookId: string,
    chapterId: string,
    limit = 40
  ): Promise<
    Array<{
      id: string;
      kind: string;
      summary: string | null;
      wordCount: number | null;
      createdAt: string;
      hasSnapshot: boolean;
      preview: string | null;
      annotations: unknown | null;
      user: { id: string; name: string | null; email: string | null } | null;
    }>
  > {
    const q = new URLSearchParams({ limit: String(Math.min(100, Math.max(1, limit))) });
    const res = await this.authorizedFetch(
      `/api/books/${encodeURIComponent(bookId.trim())}/chapters/${encodeURIComponent(chapterId.trim())}/history?${q}`
    );
    const body = await parseJson<{
      error?: string;
      history?: Array<{
        id: string;
        kind: string;
        summary: string | null;
        wordCount: number | null;
        createdAt: string;
        hasSnapshot?: boolean;
        preview?: string | null;
        annotations?: unknown | null;
        user: { id: string; name: string | null; email: string | null } | null;
      }>;
    }>(res);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(body.error || `History failed (${res.status})`);
    }
    return Array.isArray(body.history)
      ? body.history.map((row) => ({
          ...row,
          hasSnapshot: Boolean(row.hasSnapshot),
          preview: row.preview ?? null,
          annotations: row.annotations ?? null,
        }))
      : [];
  }

  /** Restore a restorable history snapshot into the live chapter. */
  async restoreChapterChangeHistory(
    bookId: string,
    chapterId: string,
    historyId: string
  ): Promise<{ ok: true; rev: number; updatedAt: string; title: string }> {
    const res = await this.authorizedFetch(
      `/api/books/${encodeURIComponent(bookId.trim())}/chapters/${encodeURIComponent(chapterId.trim())}/history/${encodeURIComponent(historyId.trim())}/restore`,
      { method: "POST" }
    );
    const body = await parseJson<{
      error?: string;
      ok?: boolean;
      rev?: number;
      updatedAt?: string;
      title?: string;
    }>(res);
    if (res.status < 200 || res.status >= 300 || !body.ok) {
      throw new Error(body.error || `Restore failed (${res.status})`);
    }
    return {
      ok: true,
      rev: typeof body.rev === "number" ? body.rev : 0,
      updatedAt: body.updatedAt || new Date().toISOString(),
      title: body.title || "",
    };
  }

  /** Append a chapter history row (e.g. overwrite annotations after seamless sync). */
  async postChapterChangeHistory(
    bookId: string,
    chapterId: string,
    payload: {
      kind?: string;
      summary?: string;
      annotations?: unknown;
      includeSnapshot?: boolean;
    }
  ): Promise<{ id: string; kind: string; createdAt: string }> {
    const res = await this.authorizedFetch(
      `/api/books/${encodeURIComponent(bookId.trim())}/chapters/${encodeURIComponent(chapterId.trim())}/history`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );
    const body = await parseJson<{
      error?: string;
      id?: string;
      kind?: string;
      createdAt?: string;
    }>(res);
    if (res.status < 200 || res.status >= 300 || !body.id) {
      throw new Error(body.error || `History post failed (${res.status})`);
    }
    return { id: body.id, kind: body.kind || "edit", createdAt: body.createdAt || new Date().toISOString() };
  }

  /** Short-lived Yjs room token for Premier live collab. */
  async getCollabRoomToken(
    bookId: string,
    chapterId: string
  ): Promise<{
    token: string;
    room: string;
    websocketUrl: string | null;
    live: boolean;
    expiresIn: string;
  }> {
    const res = await this.authorizedFetch('/api/collab/token', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ bookId: bookId.trim(), chapterId: chapterId.trim() }),
    });
    const body = await parseJson<{
      error?: string;
      token?: string;
      room?: string;
      websocketUrl?: string | null;
      live?: boolean;
      expiresIn?: string;
    }>(res);
    if (res.status < 200 || res.status >= 300 || !body.token || !body.room) {
      throw new Error(body.error || `Collab token failed (${res.status})`);
    }
    return {
      token: body.token,
      room: body.room,
      websocketUrl: body.websocketUrl ?? null,
      live: Boolean(body.live),
      expiresIn: body.expiresIn || '15m',
    };
  }

  async createBookVersion(
    bookId: string,
    params?: { name?: string; fromVersionId?: string }
  ): Promise<{
    id: string;
    bookId: string;
    name: string;
    status: string;
    parentVersionId: string | null;
    createdByUserId: string;
    createdAt: string;
    frozenAt: string | null;
    chapterCount: number;
  }> {
    const res = await this.authorizedFetch(`/api/books/${encodeURIComponent(bookId.trim())}/versions`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        ...(params?.name?.trim() ? { name: params.name.trim() } : {}),
        ...(params?.fromVersionId?.trim() ? { fromVersionId: params.fromVersionId.trim() } : {}),
      }),
    });
    const body = await parseJson<{
      error?: string;
      version?: {
        id: string;
        bookId: string;
        name: string;
        status: string;
        parentVersionId: string | null;
        createdByUserId: string;
        createdAt: string;
        frozenAt: string | null;
        chapterCount: number;
      };
    }>(res);
    if (res.status < 200 || res.status >= 300 || !body.version) {
      throw new Error(body.error || `Create version failed (${res.status})`);
    }
    return body.version;
  }

  async updateVersionBookConfig(
    bookId: string,
    versionId: string,
    bookConfig: Record<string, unknown>
  ): Promise<{ id: string; bookConfig: unknown }> {
    const res = await this.authorizedFetch(
      `/api/books/${encodeURIComponent(bookId.trim())}/versions/${encodeURIComponent(versionId.trim())}/config`,
      {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ bookConfig }),
      }
    );
    const body = await parseJson<{
      error?: string;
      version?: { id: string; bookConfig?: unknown };
    }>(res);
    if (res.status < 200 || res.status >= 300 || !body.version) {
      throw new Error(body.error || `Update version config failed (${res.status})`);
    }
    return { id: body.version.id, bookConfig: body.version.bookConfig };
  }

  async getBookVersionCatalog(
    bookId: string,
    versionId: string
  ): Promise<{
    id: string;
    name: string;
    status: string;
    chapters: Array<{
      id: string;
      title: string;
      order: number;
      kind?: string;
      chapterNumber?: number | null;
      wordCount?: number;
    }>;
  }> {
    const res = await this.authorizedFetch(
      `/api/books/${encodeURIComponent(bookId.trim())}/versions/${encodeURIComponent(versionId.trim())}`
    );
    const body = await parseJson<{
      error?: string;
      version?: {
        id: string;
        name: string;
        status: string;
        chapters?: Array<{
          id: string;
          title: string;
          order: number;
          kind?: string;
          chapterNumber?: number | null;
          wordCount?: number;
        }>;
      };
    }>(res);
    if (res.status < 200 || res.status >= 300 || !body.version) {
      throw new Error(body.error || `Version load failed (${res.status})`);
    }
    return {
      id: body.version.id,
      name: body.version.name,
      status: body.version.status,
      chapters: Array.isArray(body.version.chapters) ? body.version.chapters : [],
    };
  }

  async listEditorialEngagements(
    role: 'editor' | 'client' = 'editor'
  ): Promise<
    Array<{
      id: string;
      status: string;
      client: { id: string; name: string | null; email: string };
      grantedBooks: Array<{ bookId: string; title: string; versionId: string | null }>;
    }>
  > {
    const res = await this.authorizedFetch(`/api/editorial/engagements?role=${role}`);
    const body = await parseJson<{
      error?: string;
      engagements?: Array<{
        id: string;
        status: string;
        client: { id: string; name: string | null; email: string };
        grantedBooks: Array<{ bookId: string; title: string; versionId: string | null }>;
      }>;
    }>(res);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(body.error || `Engagements failed (${res.status})`);
    }
    return Array.isArray(body.engagements) ? body.engagements : [];
  }

  async requestEditorialEngagement(
    clientEmail: string,
    requestMessage = ''
  ): Promise<{ id: string; status: string }> {
    const res = await this.authorizedFetch('/api/editorial/engagements', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clientEmail, requestMessage }),
    });
    const body = await parseJson<{
      error?: string;
      engagement?: { id: string; status: string };
    }>(res);
    if (res.status < 200 || res.status >= 300 || !body.engagement) {
      throw new Error(body.error || `Engagement request failed (${res.status})`);
    }
    return body.engagement;
  }

  async getBookScoreBlend(bookId: string): Promise<{ mode: string; weightStoryCraft: number }> {
    const res = await this.authorizedFetch(
      `/api/books/${encodeURIComponent(bookId.trim())}/score-blend`
    );
    const body = await parseJson<{
      error?: string;
      scoreBlend?: { mode?: string; weightStoryCraft?: number };
    }>(res);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(body.error || `Score blend failed (${res.status})`);
    }
    return {
      mode: body.scoreBlend?.mode || 'storycraft',
      weightStoryCraft:
        typeof body.scoreBlend?.weightStoryCraft === 'number'
          ? body.scoreBlend.weightStoryCraft
          : 0.5,
    };
  }

  async setBookScoreBlend(
    bookId: string,
    scoreBlend: { mode: string; weightStoryCraft: number }
  ): Promise<{ mode: string; weightStoryCraft: number }> {
    const res = await this.authorizedFetch(
      `/api/books/${encodeURIComponent(bookId.trim())}/score-blend`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scoreBlend }),
      }
    );
    const body = await parseJson<{
      error?: string;
      scoreBlend?: { mode?: string; weightStoryCraft?: number };
    }>(res);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(body.error || `Score blend save failed (${res.status})`);
    }
    return {
      mode: body.scoreBlend?.mode || scoreBlend.mode,
      weightStoryCraft:
        typeof body.scoreBlend?.weightStoryCraft === 'number'
          ? body.scoreBlend.weightStoryCraft
          : scoreBlend.weightStoryCraft,
    };
  }

  async getEditorChapterFeedback(
    bookId: string,
    chapterId: string
  ): Promise<{
    chapterId: string;
    assessment: unknown;
    checklist: unknown;
    summary: string | null;
    lastUpdated: string;
    average: number | null;
  } | null> {
    const res = await this.authorizedFetch(
      `/api/books/${encodeURIComponent(bookId.trim())}/chapters/${encodeURIComponent(chapterId.trim())}/editor-feedback`
    );
    const body = await parseJson<{
      error?: string;
      feedback?: {
        chapterId: string;
        assessment: unknown;
        checklist: unknown;
        summary: string | null;
        lastUpdated: string;
        average: number | null;
      } | null;
    }>(res);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(body.error || `Editor feedback failed (${res.status})`);
    }
    return body.feedback ?? null;
  }

  async putEditorChapterFeedback(
    bookId: string,
    chapterId: string,
    payload: { assessment: unknown; summary?: string | null; checklist?: unknown }
  ): Promise<{
    chapterId: string;
    assessment: unknown;
    checklist: unknown;
    summary: string | null;
    lastUpdated: string;
    average: number | null;
  }> {
    const res = await this.authorizedFetch(
      `/api/books/${encodeURIComponent(bookId.trim())}/chapters/${encodeURIComponent(chapterId.trim())}/editor-feedback`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }
    );
    const body = await parseJson<{
      error?: string;
      feedback?: {
        chapterId: string;
        assessment: unknown;
        checklist: unknown;
        summary: string | null;
        lastUpdated: string;
        average: number | null;
      };
    }>(res);
    if (res.status < 200 || res.status >= 300 || !body.feedback) {
      throw new Error(body.error || `Editor feedback save failed (${res.status})`);
    }
    return body.feedback;
  }

  async syncBook(
    book: unknown,
    options?: { deletedChapterIds?: string[] }
  ): Promise<{
    syncedChapters: Array<{ order: number; title: string }>;
    failedChapters: Array<{ order: number; title: string; message: string }>;
    chaptersInDatabase?: number;
    chapterRevs?: Record<string, number>;
  }> {
    const res = await this.authorizedFetch('/api/desktop/books/sync', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      // The server no longer infers deletes from absence, so a chapter the user
      // actually removed has to be named explicitly.
      body: JSON.stringify({ book, deletedChapterIds: options?.deletedChapterIds ?? [] }),
    });
    const body = await parseJson<{
      error?: string;
      syncedChapters?: Array<{ order: number; title: string }>;
      failedChapters?: Array<{ order: number; title: string; message: string }>;
      chaptersInDatabase?: number;
      chapterRevs?: Record<string, number>;
    }>(res);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(body.error || `Sync failed (${res.status})`);
    }
    return {
      syncedChapters: body.syncedChapters ?? [],
      failedChapters: body.failedChapters ?? [],
      chaptersInDatabase: body.chaptersInDatabase,
      chapterRevs: body.chapterRevs,
    };
  }

  /**
   * Per-chapter save. This is the only path allowed to write chapter prose —
   * `syncBook` carries structure only. Returns the outcome rather than throwing
   * so the outbox can decide between retry, force, and park.
   */
  async patchChapter(input: {
    bookId: string;
    chapterId: string;
    title?: string;
    content?: unknown;
    wordCount?: number;
    baseRev?: number;
    force?: boolean;
  }): Promise<DesktopChapterPatchResult> {
    const path = `/api/books/${encodeURIComponent(input.bookId)}/chapters/${encodeURIComponent(
      input.chapterId
    )}`;
    let res: CloudHttpResponse;
    try {
      res = await this.authorizedFetch(path, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          title: input.title,
          content: input.content,
          wordCount: input.wordCount,
          baseRev: input.baseRev,
          force: input.force,
        }),
      });
    } catch (err) {
      // No response at all: treat as undelivered so the outbox keeps the work.
      return {
        ok: false,
        status: 0,
        offline: true,
        error: err instanceof Error ? err.message : String(err),
      };
    }

    const body = await parseJson<{
      error?: string;
      ok?: boolean;
      rev?: number;
      updatedAt?: string;
      remote?: { title: string; content: unknown; rev: number; updatedAt: string } | null;
    }>(res);

    if (res.status === 409) {
      return {
        ok: false,
        status: 409,
        remote: body.remote ?? null,
      };
    }
    if (res.status < 200 || res.status >= 300) {
      return {
        ok: false,
        status: res.status,
        error: body.error || `Chapter save failed (${res.status})`,
      };
    }
    return {
      ok: true,
      rev: typeof body.rev === 'number' ? body.rev : 0,
      updatedAt: body.updatedAt ?? new Date().toISOString(),
    };
  }

  /** Authoritative server copy of one chapter, for pulls and conflict rebase. */
  async getChapter(
    bookId: string,
    chapterId: string
  ): Promise<DesktopChapterSnapshot | null> {
    const res = await this.authorizedFetch(
      `/api/books/${encodeURIComponent(bookId)}/chapters/${encodeURIComponent(chapterId)}`
    );
    if (res.status === 404) return null;
    const body = await parseJson<{
      error?: string;
      title?: string;
      content?: unknown;
      originalContent?: unknown;
      rev?: number;
      updatedAt?: string;
    }>(res);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(body.error || `Chapter load failed (${res.status})`);
    }
    return {
      title: body.title ?? '',
      content: body.content ?? null,
      originalContent: body.originalContent ?? null,
      rev: typeof body.rev === 'number' ? body.rev : 0,
      updatedAt: body.updatedAt ?? new Date().toISOString(),
    };
  }

  async getBillingPlan(): Promise<{
    plan: 'free' | 'author' | 'editor';
    display: { name: string; price: number; priceYearly: number };
    hasStripe: boolean;
    planExpiresAt: string | null;
    limits: {
      tokensPerMonth: number | null;
      audioPerMonth: number | null;
      coversPerMonth: number | null;
      voices: number;
    } | null;
    usage: {
      tokensUsed?: number;
      tokensReserved?: number;
      bonusTokensGranted?: number;
      bonusTokensUsed?: number;
      [key: string]: unknown;
    } | null;
    tokens: {
      used: number;
      reserved: number;
      limit: number;
      remaining: number;
      bonusRemaining: number;
    } | null;
    entitlements: {
      publishPro: boolean;
      publishProPurchasedAt: string | null;
    };
    referral: {
      code: string;
      sharePath: string;
      shareUrl: string;
    } | null;
  }> {
    const res = await this.authorizedFetch('/api/user/plan', { method: 'GET' });
    const body = await parseJson<{
      error?: string;
      plan?: 'free' | 'author' | 'editor' | 'publisher';
      display?: { name: string; price: number; priceYearly: number };
      hasStripe?: boolean;
      planExpiresAt?: string | null;
      limits?: {
        tokensPerMonth: number | null;
        audioPerMonth: number | null;
        coversPerMonth: number | null;
        voices: number;
      };
      usage?: Record<string, unknown>;
      tokens?: {
        used?: number;
        reserved?: number;
        limit?: number;
        remaining?: number;
        bonusRemaining?: number;
      };
      entitlements?: {
        publishPro?: boolean;
        publishProPurchasedAt?: string | null;
      };
      referral?: {
        code?: string;
        sharePath?: string;
      };
    }>(res);
    if (res.status < 200 || res.status >= 300 || !body.plan || !body.display) {
      throw new Error(body.error || `Plan lookup failed (${res.status})`);
    }
    const plan = body.plan === 'publisher' ? 'editor' : body.plan;
    const tokens =
      body.tokens && typeof body.tokens === 'object'
        ? {
            used: Number(body.tokens.used ?? 0),
            reserved: Number(body.tokens.reserved ?? 0),
            limit: Number(body.tokens.limit ?? 0),
            remaining: Number(body.tokens.remaining ?? 0),
            bonusRemaining: Number(body.tokens.bonusRemaining ?? 0),
          }
        : null;
    const baseUrl = await this.getBaseUrl();
    const referral =
      body.referral?.code
        ? {
            code: body.referral.code,
            sharePath: body.referral.sharePath ?? `/r/${body.referral.code}`,
          }
        : null;
    return {
      plan,
      display: body.display,
      hasStripe: Boolean(body.hasStripe),
      planExpiresAt: body.planExpiresAt ?? null,
      limits: body.limits ?? null,
      usage: body.usage ?? null,
      tokens,
      entitlements: {
        publishPro: Boolean(body.entitlements?.publishPro) || plan === 'editor',
        publishProPurchasedAt: body.entitlements?.publishProPurchasedAt ?? null,
      },
      referral: referral
        ? {
            ...referral,
            shareUrl: `${baseUrl}${referral.sharePath.startsWith('/') ? '' : '/'}${referral.sharePath}`,
          }
        : null,
    };
  }

  async getFeatureFlags(): Promise<string[]> {
    const res = await this.authorizedFetch('/api/user/flags', { method: 'GET' });
    const body = await parseJson<{ error?: string; flags?: string[] }>(res);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(body.error || `Feature flags failed (${res.status})`);
    }
    return Array.isArray(body.flags) ? body.flags : [];
  }

  /** Opens Stripe Checkout for the one-time Publish Pro add-on. */
  async startPublishProCheckout(): Promise<string> {
    const res = await this.authorizedFetch('/api/stripe/checkout', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ product: 'publish_pro' }),
    });
    const body = await parseJson<{ error?: string; url?: string }>(res);
    if (res.status < 200 || res.status >= 300 || !body.url) {
      throw new Error(body.error || `Publish Pro checkout failed (${res.status})`);
    }
    await this.platform.openExternalUrl(body.url);
    return body.url;
  }

  /** Opens Stripe Checkout in the system browser for Author or Editor. */
  async startCheckout(
    plan: 'author' | 'editor',
    interval: 'month' | 'year' = 'month'
  ): Promise<string> {
    const res = await this.authorizedFetch('/api/stripe/checkout', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ plan, interval }),
    });
    const body = await parseJson<{ error?: string; url?: string }>(res);
    if (res.status < 200 || res.status >= 300 || !body.url) {
      throw new Error(body.error || `Checkout failed (${res.status})`);
    }
    await this.platform.openExternalUrl(body.url);
    return body.url;
  }

  /** Opens the Stripe Customer Portal in the system browser. */
  async openBillingPortal(): Promise<string> {
    const res = await this.authorizedFetch('/api/stripe/portal', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    });
    const body = await parseJson<{ error?: string; url?: string }>(res);
    if (res.status < 200 || res.status >= 300 || !body.url) {
      throw new Error(body.error || `Billing portal failed (${res.status})`);
    }
    await this.platform.openExternalUrl(body.url);
    return body.url;
  }

  async getBetaReadProgram(bookId: string): Promise<{
    program: BookBetaReadProgramConfig;
    slug: string;
    betaReadSlugOverride: string;
  }> {
    const id = bookId.trim();
    const res = await this.authorizedFetch(`/api/books/${encodeURIComponent(id)}/beta-read-program`);
    const body = await parseJson<{
      error?: string;
      program?: BookBetaReadProgramConfig;
      slug?: string;
      betaReadSlugOverride?: string;
    }>(res);
    if (res.status < 200 || res.status >= 300 || !body.program) {
      throw new Error(body.error || `Beta read program lookup failed (${res.status})`);
    }
    return {
      program: body.program,
      slug: body.slug ?? id,
      betaReadSlugOverride: body.betaReadSlugOverride ?? '',
    };
  }

  async updateBetaReadProgram(
    bookId: string,
    program: BookBetaReadProgramConfig & { betaReadSlugOverride?: string }
  ): Promise<{ program: BookBetaReadProgramConfig; slug: string }> {
    const id = bookId.trim();
    const res = await this.authorizedFetch(`/api/books/${encodeURIComponent(id)}/beta-read-program`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(program),
    });
    const body = await parseJson<{
      error?: string;
      program?: BookBetaReadProgramConfig;
      slug?: string;
    }>(res);
    if (res.status < 200 || res.status >= 300 || !body.program) {
      throw new Error(body.error || `Beta read program save failed (${res.status})`);
    }
    return { program: body.program, slug: body.slug ?? id };
  }

  async listBetaInvites(bookId: string): Promise<DesktopBetaInvite[]> {
    const res = await this.authorizedFetch(
      `/api/books/${encodeURIComponent(bookId.trim())}/beta-invites`
    );
    const body = await parseJson<{ error?: string; invites?: DesktopBetaInvite[] }>(res);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(body.error || `Beta invites lookup failed (${res.status})`);
    }
    return Array.isArray(body.invites) ? body.invites : [];
  }

  async createBetaInvite(
    bookId: string,
    email: string
  ): Promise<{
    invite: DesktopBetaInvite;
    inviteUrl: string;
    inviteCode: string;
    emailSent: boolean;
    emailStatusMessage?: string;
  }> {
    const res = await this.authorizedFetch(
      `/api/books/${encodeURIComponent(bookId.trim())}/beta-invites`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      }
    );
    const body = await parseJson<{
      error?: string;
      invite?: DesktopBetaInvite;
      inviteUrl?: string;
      inviteCode?: string;
      emailSent?: boolean;
      emailStatusMessage?: string;
    }>(res);
    if (res.status < 200 || res.status >= 300 || !body.invite || !body.inviteUrl || !body.inviteCode) {
      throw new Error(body.error || `Create beta invite failed (${res.status})`);
    }
    return {
      invite: body.invite,
      inviteUrl: body.inviteUrl,
      inviteCode: body.inviteCode,
      emailSent: Boolean(body.emailSent),
      emailStatusMessage: body.emailStatusMessage,
    };
  }

  async listBetaApplications(bookId: string): Promise<DesktopBetaApplication[]> {
    const res = await this.authorizedFetch(
      `/api/books/${encodeURIComponent(bookId.trim())}/beta-read-applications`
    );
    const body = await parseJson<{ error?: string; applications?: DesktopBetaApplication[] }>(res);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(body.error || `Beta applications lookup failed (${res.status})`);
    }
    return Array.isArray(body.applications) ? body.applications : [];
  }

  async approveBetaApplication(bookId: string, applicationId: string): Promise<void> {
    const res = await this.authorizedFetch(
      `/api/books/${encodeURIComponent(bookId.trim())}/beta-read-applications/${encodeURIComponent(applicationId.trim())}/approve`,
      { method: 'POST' }
    );
    const body = await parseJson<{ error?: string }>(res);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(body.error || `Approve failed (${res.status})`);
    }
  }

  async rejectBetaApplication(bookId: string, applicationId: string): Promise<void> {
    const res = await this.authorizedFetch(
      `/api/books/${encodeURIComponent(bookId.trim())}/beta-read-applications/${encodeURIComponent(applicationId.trim())}/reject`,
      { method: 'POST' }
    );
    const body = await parseJson<{ error?: string }>(res);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(body.error || `Reject failed (${res.status})`);
    }
  }

  async getBetaEndSurvey(bookId: string): Promise<DesktopBetaEndSurvey> {
    const res = await this.authorizedFetch(
      `/api/books/${encodeURIComponent(bookId.trim())}/beta-end-survey`
    );
    const body = await parseJson<{ error?: string; survey?: DesktopBetaEndSurvey }>(res);
    if (res.status < 200 || res.status >= 300 || !body.survey) {
      throw new Error(body.error || `Beta end survey lookup failed (${res.status})`);
    }
    return body.survey;
  }

  async updateBetaEndSurvey(
    bookId: string,
    questions: BetaReaderQuestionnaireQuestion[]
  ): Promise<DesktopBetaEndSurvey> {
    const res = await this.authorizedFetch(
      `/api/books/${encodeURIComponent(bookId.trim())}/beta-end-survey`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questions }),
      }
    );
    const body = await parseJson<{ error?: string; survey?: DesktopBetaEndSurvey }>(res);
    if (res.status < 200 || res.status >= 300 || !body.survey) {
      throw new Error(body.error || `Beta end survey save failed (${res.status})`);
    }
    return body.survey;
  }

  fullSignOut(): void {
    localStorage.removeItem(LS_ACCESS);
    localStorage.removeItem(LS_ACCESS_EXPIRES_AT);
    localStorage.removeItem(LS_USER_ID);
    localStorage.removeItem(LS_USER_EMAIL);
    void this.platform.setRefreshToken(null);
    try {
      // Prevent immediate silent SSO after an intentional SPA sign-out.
      sessionStorage.setItem(SPA_SSO_SKIP_KEY, '1');
    } catch {
      /* ignore */
    }
  }

  async getLocalCopy(bookId: string, machineId: string): Promise<DesktopLocalCopy | null> {
    const id = bookId.trim();
    const mid = machineId.trim();
    if (!id || !mid) return null;
    const res = await this.authorizedFetch(
      `/api/desktop/books/${encodeURIComponent(id)}/local-copy?machineId=${encodeURIComponent(mid)}`
    );
    const body = await parseJson<{ error?: string; localCopy?: DesktopLocalCopy | null }>(res);
    if (res.status < 200 || res.status >= 300) {
      throw new Error(body.error || `Local copy lookup failed (${res.status})`);
    }
    return body.localCopy ?? null;
  }

  async registerLocalCopy(
    bookId: string,
    payload: { machineId: string; machineName: string; fileName: string; filePath: string }
  ): Promise<DesktopLocalCopy> {
    const id = bookId.trim();
    if (!id) throw new Error('bookId is required');
    const res = await this.authorizedFetch(`/api/desktop/books/${encodeURIComponent(id)}/local-copy`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const body = await parseJson<{ error?: string; localCopy?: DesktopLocalCopy }>(res);
    if (res.status < 200 || res.status >= 300 || !body.localCopy) {
      throw new Error(body.error || `Local copy register failed (${res.status})`);
    }
    return body.localCopy;
  }
}
